import { memo } from "react";
import Chip from "@mui/material/Chip";
import type { BatchJobItemReview, FieldReannotationOutcome } from "../../../../lib/corpusApi";

type BadgeColor = "default" | "success" | "warning";

const OUTCOME_MAP: Record<FieldReannotationOutcome, { label: string; color: BadgeColor }> = {
  updated: { label: "Updated", color: "success" },
  unchanged: { label: "Unchanged", color: "default" },
  contradicts_human: { label: "Contradicts a person", color: "warning" },
  skipped: { label: "Skipped", color: "default" },
};

/** Where a contradiction stands once someone has settled it — applying it overrides the person's
 *  label with the AI's, dismissing it keeps the person's. */
const SETTLED_CONTRADICTION_MAP: Record<
  Exclude<BatchJobItemReview, "open">,
  { label: string; color: BadgeColor }
> = {
  applied: { label: "Overridden", color: "success" },
  dismissed: { label: "Kept", color: "default" },
};

export interface FieldReannotationOutcomeBadgeProps {
  outcome: FieldReannotationOutcome;
  /** The item's review state. Only a contradiction has one; once settled it replaces the outcome. */
  review?: BatchJobItemReview | null;
}

/** Chip rendering what re-annotating a field did to one document — and, for a contradiction with a
 *  person's label, how it was settled. */
export const FieldReannotationOutcomeBadge = memo(function FieldReannotationOutcomeBadge({
  outcome,
  review = null,
}: FieldReannotationOutcomeBadgeProps) {
  const settled =
    outcome === "contradicts_human" && review && review !== "open"
      ? SETTLED_CONTRADICTION_MAP[review]
      : null;
  const { label, color } = settled ?? OUTCOME_MAP[outcome];
  return <Chip label={label} color={color} size="small" />;
});
